import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CircleNotch, Printer, ListBullets } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "@/lib/api";

export default function LegalPage({ slug }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [otherPages, setOtherPages] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setPage(null);
    api
      .get(`/legal/${slug}`)
      .then((r) => setPage(r.data.page))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    api
      .get("/legal")
      .then((r) => setOtherPages(r.data.pages || []))
      .catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (page?.title) {
      const original = document.title;
      document.title = `${page.title} — Mine AI`;
      return () => {
        document.title = original;
      };
    }
  }, [page]);

  // Build a simple table of contents from H2 headings in the markdown
  const toc = useMemo(() => {
    if (!page?.content) return [];
    const items = [];
    const re = /^##\s+(.+)$/gm;
    let m;
    while ((m = re.exec(page.content)) !== null) {
      const text = m[1].trim();
      const id = text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      items.push({ text, id });
    }
    return items;
  }, [page]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 flex items-center gap-3 text-[#4A5568]">
        <CircleNotch className="animate-spin" weight="bold" /> Loading...
      </div>
    );
  }
  if (notFound || !page) {
    return (
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20" data-testid="legal-not-found">
        <h1 className="font-display text-4xl text-[#0A1128]">Policy not found</h1>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 text-[#3B5BDB]">
          <ArrowLeft size={16} /> Back home
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={`legal-page-${slug}`}>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-[#4A5568] hover:text-[#3B5BDB] inline-flex items-center gap-2 smooth"
          data-testid="legal-back"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <div className="grid lg:grid-cols-[1fr_280px] gap-10 mt-6">
          <article className="min-w-0">
            <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold">
              Mine AI · Legal
            </p>
            <h1 className="font-display text-4xl sm:text-5xl mt-3 text-[#0A1128] tracking-tight leading-[1.1]">
              {page.title}
            </h1>
            {page.summary && (
              <p className="mt-3 text-base text-[#4A5568]">{page.summary}</p>
            )}
            <div className="mt-3 text-xs text-[#4A5568] flex items-center gap-3">
              {page.updated_at && (
                <span>
                  Last updated{" "}
                  {new Date(page.updated_at).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              )}
              <button
                onClick={() => window.print()}
                className="inline-flex items-center gap-1 hover:text-[#3B5BDB] smooth print:hidden"
                data-testid="legal-print"
              >
                <Printer size={12} /> Print
              </button>
            </div>

            <div
              className="mt-10 prose-mineai legal-content"
              data-testid="legal-content"
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h2: ({ node, children, ...props }) => {
                    const text = String(children);
                    const id = text
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")
                      .replace(/^-|-$/g, "");
                    return (
                      <h2 id={id} {...props}>
                        {children}
                      </h2>
                    );
                  },
                }}
              >
                {page.content}
              </ReactMarkdown>
            </div>
          </article>

          <aside className="hidden lg:block print:hidden">
            <div className="sticky top-24 space-y-6">
              {toc.length > 0 && (
                <div className="mc-card p-5" data-testid="legal-toc">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#3B5BDB] font-semibold flex items-center gap-1.5">
                    <ListBullets size={12} weight="bold" /> Contents
                  </p>
                  <ul className="mt-3 space-y-1.5">
                    {toc.map((t) => (
                      <li key={t.id}>
                        <a
                          href={`#${t.id}`}
                          className="text-xs text-[#4A5568] hover:text-[#3B5BDB] smooth block leading-snug"
                        >
                          {t.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mc-card p-5">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#3B5BDB] font-semibold">
                  Other policies
                </p>
                <ul className="mt-3 space-y-1.5">
                  {otherPages
                    .filter((p) => p.slug !== slug)
                    .map((p) => (
                      <li key={p.slug}>
                        <Link
                          to={`/${p.slug}`}
                          className="text-xs text-[#0A1128] hover:text-[#3B5BDB] smooth block leading-snug"
                        >
                          {p.title}
                        </Link>
                      </li>
                    ))}
                </ul>
                <div className="mt-4 pt-4 border-t border-[#E8DFD1] text-[11px] text-[#4A5568] leading-relaxed">
                  Auromine Retail POS Pvt Ltd
                  <br />
                  Chennai, Tamil Nadu, India
                  <br />
                  hello@mineai.io
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
